import {
  IsString,
  IsNumber,
  IsIn,
  IsBoolean,
  IsOptional,
  IsDefined,
  ValidateNested,
  Min,
  Max,
} from 'class-validator';
import { Type } from 'class-transformer';
import { IsRgbaOrReference } from '@builder4/shared/dto/styles/validators/IsRgbaOrReference';
import {
  VisibilityStateDto,
  PositionAnchorsStateDto,
  InteractionEventDto,
  ShadowSettingsDto,
  SHADOW_TYPE_VALUES,
  type ShadowType,
} from '@builder4/shared/dto/craft/props/shared';
import { IsTRBLNumberCoords } from '@builder4/shared/dto/craft/validators/IsTRBLNumberCoords';
import { IsVideoUrl } from '@builder4/shared/dto/craft/validators/IsVideoUrl';
import { IsVideoEmbedCode } from '@builder4/shared/dto/craft/validators/IsVideoEmbedCode';
import { RgbaDto } from '@builder4/shared/dto/styles';
import {
  ANIMATION_TYPES,
  ANIMATION_EASINGS,
  HOVER_EFFECT_TYPES,
} from '@builder4/shared/types/animation';

const POSITION_VALUES = ['relative', 'absolute', 'fixed', 'sticky'] as const;
const SOURCE_TYPE_VALUES = ['url', 'embed'] as const;
export const VIDEO_PROVIDER_VALUES = [
  'youtube',
  'vimeo',
  'loom',
  'wistia',
  'vidyard',
  'brightcove',
  'cloudflare-stream',
] as const;
const FIT_TYPE_VALUES = ['fill', 'fit'] as const;
const FILL_SPACE_VALUES = ['yes', 'no'] as const;
const MAX_WIDTH_UNIT_VALUES = ['px', '%'] as const;

export class VideoBreakpointPropsDto {
  @IsOptional()
  @IsString()
  width?: string;

  @IsOptional()
  @IsString()
  height?: string;

  @IsOptional()
  @IsIn([...FILL_SPACE_VALUES])
  fillSpace?: 'yes' | 'no';

  @IsOptional()
  @IsString()
  flexBasis?: string;

  @IsOptional()
  @IsTRBLNumberCoords()
  padding?: [number, number, number, number];

  @IsOptional()
  @IsTRBLNumberCoords()
  margin?: [number, number, number, number];

  @IsOptional()
  @IsTRBLNumberCoords()
  borderRadius?: [number, number, number, number];

  @IsOptional()
  @IsIn([...POSITION_VALUES])
  position?: 'relative' | 'absolute' | 'fixed' | 'sticky';

  @IsOptional()
  @ValidateNested()
  @Type(() => PositionAnchorsStateDto)
  positionAnchorsState?: PositionAnchorsStateDto;

  @IsOptional()
  @IsString()
  left?: string;

  @IsOptional()
  @IsString()
  right?: string;

  @IsOptional()
  @IsString()
  top?: string;

  @IsOptional()
  @IsString()
  bottom?: string;

  @IsOptional()
  @IsNumber()
  @Min(-1)
  @Max(100)
  zIndex?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  maxWidth?: number;

  @IsOptional()
  @IsIn([...MAX_WIDTH_UNIT_VALUES])
  maxWidthUnit?: (typeof MAX_WIDTH_UNIT_VALUES)[number];
}

export class VideoPropsDto {
  // Source
  @IsDefined()
  @IsIn([...SOURCE_TYPE_VALUES])
  sourceType!: 'url' | 'embed';

  @IsDefined()
  @IsVideoUrl()
  url!: string;

  @IsDefined()
  @IsVideoEmbedCode()
  embedCode!: string;

  @IsDefined()
  @IsIn([...VIDEO_PROVIDER_VALUES])
  videoProvider!: (typeof VIDEO_PROVIDER_VALUES)[number];

  @IsOptional()
  @IsString()
  thumbnailUrl?: string;

  // Playback
  @IsDefined()
  @IsBoolean()
  autoplay!: boolean;

  @IsOptional()
  @IsBoolean()
  loop?: boolean;

  @IsOptional()
  @IsBoolean()
  mute?: boolean;

  @IsDefined()
  @IsBoolean()
  controls!: boolean;

  // Display
  @IsDefined()
  @IsIn([...FIT_TYPE_VALUES])
  fitType!: 'fill' | 'fit';

  @IsDefined()
  @IsNumber()
  @Min(0)
  @Max(1)
  opacity!: number;

  // Dimensions
  @IsDefined()
  @IsString()
  width!: string;

  @IsDefined()
  @IsString()
  height!: string;

  @IsDefined()
  @IsNumber()
  @Min(0)
  minWidth!: number;

  @IsDefined()
  @IsNumber()
  @Min(0)
  minHeight!: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  maxWidth?: number;

  @IsOptional()
  @IsIn([...MAX_WIDTH_UNIT_VALUES])
  maxWidthUnit?: (typeof MAX_WIDTH_UNIT_VALUES)[number];

  @IsDefined()
  @IsBoolean()
  aspectRatioLock!: boolean;

  @IsOptional()
  @IsNumber()
  @Min(0)
  aspectRatio?: number;

  // Layout
  @IsDefined()
  @IsTRBLNumberCoords()
  padding!: [number, number, number, number];

  @IsDefined()
  @IsTRBLNumberCoords()
  margin!: [number, number, number, number];

  @IsDefined()
  @IsTRBLNumberCoords()
  borderRadius!: [number, number, number, number];

  @IsDefined()
  @IsNumber()
  @Min(0)
  borderWidth!: number;

  @IsOptional()
  @IsRgbaOrReference()
  borderColor?: RgbaDto | string;

  @IsOptional()
  @IsRgbaOrReference()
  backgroundColor?: RgbaDto | string;

  // Position
  @IsDefined()
  @IsIn([...POSITION_VALUES])
  position!: 'relative' | 'absolute' | 'fixed' | 'sticky';

  @IsOptional()
  @IsString()
  top?: string;

  @IsOptional()
  @IsString()
  left?: string;

  @IsOptional()
  @IsString()
  right?: string;

  @IsOptional()
  @IsString()
  bottom?: string;

  @IsDefined()
  @ValidateNested()
  @Type(() => PositionAnchorsStateDto)
  positionAnchorsState!: PositionAnchorsStateDto;

  @IsDefined()
  @IsNumber()
  @Min(-1)
  @Max(100)
  zIndex!: number;

  // Flex
  @IsDefined()
  @IsIn([...FILL_SPACE_VALUES])
  fillSpace!: 'yes' | 'no';

  @IsDefined()
  @IsString()
  flexBasis!: string;

  @IsDefined()
  @IsNumber()
  @Min(0)
  flexGrow!: number;

  // Visibility
  @IsDefined()
  @ValidateNested()
  @Type(() => VisibilityStateDto)
  visibility!: VisibilityStateDto;

  // Interaction
  @IsOptional()
  @ValidateNested()
  @Type(() => InteractionEventDto)
  clickEvent?: InteractionEventDto;

  // Animation
  @IsOptional()
  @IsIn([...ANIMATION_TYPES])
  animationType?: (typeof ANIMATION_TYPES)[number];

  @IsOptional()
  @IsNumber()
  @Min(100)
  @Max(2000)
  animationDuration?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(2000)
  animationDelay?: number;

  @IsOptional()
  @IsIn([...ANIMATION_EASINGS])
  animationEasing?: (typeof ANIMATION_EASINGS)[number];

  // Hover effect
  @IsOptional()
  @IsIn([...HOVER_EFFECT_TYPES])
  hoverEffect?: (typeof HOVER_EFFECT_TYPES)[number];

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(2000)
  hoverTransitionDuration?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(200)
  hoverIntensity?: number;
  // Shadow
  @IsOptional()
  @IsIn([...SHADOW_TYPE_VALUES])
  shadowType?: ShadowType;

  @IsOptional()
  @ValidateNested()
  @Type(() => ShadowSettingsDto)
  shadowSettings?: ShadowSettingsDto;

  // Breakpoint overrides (optional, partial props)
  @IsOptional()
  @ValidateNested()
  @Type(() => VideoBreakpointPropsDto)
  tablet?: VideoBreakpointPropsDto;

  @IsOptional()
  @ValidateNested()
  @Type(() => VideoBreakpointPropsDto)
  mobile?: VideoBreakpointPropsDto;
}
